import axios from "axios";
import { TMDB_CONFIG } from "../constants/tmdb";
import { getOfficialTrailer } from "../utils/movieUtils";

/** @typedef {import("../types").Movie} Movie */
/** @typedef {import("../types").Video} Video */

const withApiKey = (params = {}) => ({
  ...params,
  api_key: TMDB_CONFIG.apiKey,
});

/**
 * Fábrica del repositorio de películas. Recibe la config de conexión y devuelve
 * un objeto con la misma forma sin importar el proveedor concreto, para que los
 * hooks puedan depender de esta abstracción (y en tests inyectar un doble) en
 * vez de acoplarse a axios/TMDB directamente.
 * @param {{ baseUrl?: string, timeout?: number }} [config]
 */
export function createTmdbMovieRepository({
  baseUrl = TMDB_CONFIG.baseUrl,
  timeout = 10000,
} = {}) {
  const client = axios.create({ baseURL: baseUrl, timeout });

  return {
    /** @returns {Promise<Movie[]>} */
    async discoverMovies() {
      const { data } = await client.get("/discover/movie", {
        params: withApiKey(),
      });

      return data.results || [];
    },

    /** @param {string} query @returns {Promise<Movie[]>} */
    async searchMoviesByQuery(query) {
      const { data } = await client.get("/search/movie", {
        params: withApiKey({ query }),
      });

      return data.results || [];
    },

    /** @param {number} movieId @returns {Promise<Movie>} */
    async getMovieDetails(movieId) {
      const { data } = await client.get(`/movie/${movieId}`, {
        params: withApiKey({ append_to_response: "videos" }),
      });

      return data;
    },

    /** @param {number} movieId @returns {Promise<{movie: Movie, trailer: Video|null}>} */
    async getMovieWithTrailer(movieId) {
      const details = await this.getMovieDetails(movieId);
      const trailer = getOfficialTrailer(details?.videos?.results || []);

      return { movie: details, trailer };
    },
  };
}

/** Instancia por defecto usada en producción; los hooks pueden reemplazarla por inyección. */
export const movieRepository = createTmdbMovieRepository();
